import { forwardRef, Inject, Injectable } from '@angular/core';
import { NgbModal }         from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';

import { ApiService }             from '../api/api.service';
import { CarfaxHistoryComponent } from './carfax.history.component';
import { CompanyService }         from '../companies/company.service';
import { SessionService }         from '../sessions/session.service';
import { VehicleModel }           from '../vehicles/models/vehicle';

@Injectable({
  providedIn: 'root'
})
export class CarfaxService {
  public title = this.translateService.instant('COMPANY_SPACE.INTEGRATIONS.CARFAX.CARFAX');

  constructor (
    @Inject(forwardRef(() => ApiService)) private apiService      : ApiService,
    @Inject(forwardRef(() => CompanyService))
    private companyService  : CompanyService,
    private modalService    : NgbModal,
    @Inject(forwardRef(() => SessionService))
    private sessionService  : SessionService,
    private translateService: TranslateService
  ) {}

  activate () : ng.IPromise<any> {
    return this.apiService.get('/company/add_carfax')
      .then(() => this.sessionService.refresh());
  }

  authorize () : ng.IPromise<any> {
    return this.apiService.get('/carfax/authorisation_request_quickvin')
      .then(response => response.carfax_response.success
        ? Promise.resolve(`${ response.carfax_response.success }${ this.translateService.instant('JS_SPACE.CARFAX.CARFAX_VERSION', {
            name: 'Carfax QuickVIN'
          }) }`)
        : Promise.reject(`${ response.carfax_response.error.code } - ${ response.carfax_response.error.message }${ this.translateService.instant('JS_SPACE.CARFAX.CARFAX_VERSION', {
            name: 'QuickVIN'
          }) }`)
      )
      .then(() => this.apiService.get('/carfax/authorisation_request_history'))
      .then(response => response.carfax_response.success
        ? Promise.resolve(`${ response.carfax_response.success }${ this.translateService.instant('JS_SPACE.CARFAX.CARFAX_VERSION', {
            name: 'Carfax Service History'
          }) }`)
        : Promise.reject(`${ response.carfax_response.error.code} - ${response.carfax_response.error.message}${ this.translateService.instant('JS_SPACE.CARFAX.CARFAX_VERSION', {
            name: 'Service History'
          }) }`)
      );
  }

  deactivate () : ng.IPromise<any> {
    return this.apiService.get('/company/remove_carfax')
      .then(() => this.sessionService.refresh());
  }

  getActivateMessage () : string {
    return `
      ${ this.translateService.instant('JS_SPACE.CARFAX.ACCEPTING_CONFIRM') }
      <a href='https://service.carfax.com/csn/csnTerms' target="_blank" style="text-decoration:underline">
        ${ this.translateService.instant('JS_SPACE.CARFAX.TERMS_CONDITIONS') }
      </a>
      ${ this.translateService.instant('JS_SPACE.CARFAX.CERTIFY') }
    `;
  }

  getSignupMessage ( vehicle : VehicleModel ) : ng.IPromise<string> {
    return this.apiService.post('/vehicles/get_carfax_signup_message', vehicle)
      .then(result => `${ result.message.intro } ${ result.message.outro }`);
  }

  getStatus ( vehicle : VehicleModel ) : ng.IPromise<string> {
    if ( !vehicle.vin ) {
      return Promise.reject(this.translateService.instant('JS_SPACE.CARFAX.PROVIDE_VIN'));
    }

    return this.apiService.post('/vehicles/find_carfax_signup_status', vehicle)
      .then(result => {
        let status: string;

        if ( !result.accountExists ) {
          status = 'customerSignup';
        }
        else if ( result.accountExists ) {
          status = result.vehicleExists
            ? 'user'
            : 'addVehicle';
        }
        else {
          status = 'error';
        }

        return status;
      });
  }

  findHistory ( vehicle : VehicleModel ) : ng.IPromise<string> {
    if ( !vehicle.vin ) {
      return Promise.reject(this.translateService.instant('JS_SPACE.CARFAX.PROVIDE_VIN'));
    }

    return this.apiService.post('/vehicles/find_history_for_carfax', vehicle)
      .then(history => {
        if ( !history || history === 'null' ) {
          return Promise.reject('No History Available');
        }

        const modalRef = this.modalService.open(CarfaxHistoryComponent, {
          windowClass: 'product-search-modal flip-container'
        });

        modalRef.componentInstance.displayRecords    = history.serviceHistory.displayRecords;
        modalRef.componentInstance.serviceCategories = history.serviceHistory.serviceCategories;

        return modalRef.result;
      });
  }

  saveSettings ( company : any ) : ng.IPromise<any> {
    return this.apiService.patch('/company', company, 'company')
      .then(() => this.apiService.patch('/company_integrations', {
        use_carfax_loyalty: company.carfax_settings.use_carfax_loyalty
      }, 'company_integration'))
      .then(() => this.sessionService.refresh());
  }

  search ( vehicle : VehicleModel ) : ng.IPromise<any> {
    return !vehicle.plate_number && !vehicle.vin
      ? Promise.reject(this.translateService.instant('JS_SPACE.CHECKA.PROVIDE_REGO_OR_VIN_NUMBER'))
      : this.apiService.post('/vehicles/find_on_carfax', vehicle);
  }

  signup ( vehicle : VehicleModel ) : ng.IPromise<any> {
    return this.apiService.post('/vehicles/signup_customer_and_vehicle_to_carfax', vehicle)
      .then(result => {
        const response = {} as any;

        if ( result.accountCreated ) {
          if ( result.vehicleCreated ) {
            response.message = this.translateService.instant('COMPANY_SPACE.INTEGRATIONS.CARFAX.SIGNUP.CUSTOMER_AND_VEHICLE');
            response.type    = 'success';
          }
          else {
            response.message = this.translateService.instant('COMPANY_SPACE.INTEGRATIONS.CARFAX.SIGNUP.CUSTOMER_NO_VEHICLE');
            response.type    = 'warning';
          };
        }
        else {
          if ( result.vehicleCreated ) {
            response.message = this.translateService.instant('COMPANY_SPACE.INTEGRATIONS.CARFAX.SIGNUP.VEHICLE_ADDED');
            response.type    = 'success';
          }
          else {
            return Promise.reject(this.translateService.instant('COMPANY_SPACE.INTEGRATIONS.CARFAX.SIGNUP.CUSTOMER_NOT_SIGNED_UP'));
          };
        };

        return response;
      });
  }

  useLoyalty ( company = this.companyService.company ) : boolean {
    return company.carfax_settings.use_carfax_loyalty;
  }
}
